import { CalendarDate, DateFormatter, parseAbsolute } from '@internationalized/date';
import { getEnumKey, type EnumType, type ValueOf } from '@libs/shared/common/helpers/enum.helper';
import {
  capitalize as rCapitalize,
  split as rSplit,
  pipe as rPipe,
  join as rJoin,
  toLowerCase as rToLowerCase,
  purry as rPurry,
} from 'remeda';

import { LOCALE } from '@/constants/locale';
import { theme } from '@/constants/theme';
import { timezone } from '@/utils/timezone.util';

type DateInput = Date | string;
type DateFormat = keyof typeof theme.dateFormat;

const normalizeAmPm = (str: string): string => str
  .replace(/\sAM/g, 'am')
  .replace(/\sPM/g, 'pm');

const formatWithTimeParts = (jsDate: Date): string => {
  const tz = timezone.get();
  const formatter = new DateFormatter(LOCALE, { ...theme.dateFormat.withTime, timeZone: tz });
  const parts = formatter.formatToParts(jsDate);
  const get = (type: string): string => parts.find((p) => p.type === type)?.value ?? '';
  const time = `${get('hour')}:${get('minute')}${normalizeAmPm(` ${get('dayPeriod')}`).trim()}`;
  const date = `${get('month')} ${get('day')}, ${get('year')}`;
  return `${time} ${date}`;
};

/**
 * Formats a date in the user's global timezone. Reads timezone from the global set at auth time.
 */
export const formatDate = (format: DateFormat) => (date: DateInput): string => {
  const jsDate = typeof date === 'string' ? new Date(date) : date;
  if (format === 'withTime') {
    return formatWithTimeParts(jsDate);
  }
  const formatter = new DateFormatter(LOCALE, { ...theme.dateFormat[format], timeZone: timezone.get() });
  return normalizeAmPm(formatter.format(jsDate));
};

/**
 * Formats a UTC ISO datetime as a calendar date in the given IANA timezone.
 * Use for campaign start/end dates where the backend stores start-of-day UTC instants.
 */
export const formatDateInTimezone = (format: DateFormat, timeZone: string) => (isoString: string): string => {
  const { year, month, day } = parseAbsolute(isoString, timeZone);
  const formatter = new DateFormatter(LOCALE, { ...theme.dateFormat[format], timeZone });
  return normalizeAmPm(formatter.format(new CalendarDate(year, month, day).toDate(timeZone)));
};

type NumberFormat = 'decimal' | 'currency';

const formatNumberImpl = (amount: number, format: NumberFormat): string =>
  new Intl.NumberFormat(LOCALE, {
    style: format,
    currency: format === 'currency' ? 'USD' : undefined,
    minimumFractionDigits: format === 'currency' ? 2 : 0,
    maximumFractionDigits: format === 'currency' ? 2 : 0,
  })
    .format(amount)
    .replace('$', '');

export function formatNumber(amount: number, format: NumberFormat): string;
export function formatNumber(format: NumberFormat): (amount: number) => string;
export function formatNumber(...args: readonly unknown[]): unknown {
  return rPurry(formatNumberImpl, args);
}

type ReplaceUnderscores<S extends string> =
  S extends `${infer A}_${infer B}`
    ? `${A} ${ReplaceUnderscores<B>}`
    : S;

type FormattedEnumKey<S extends string> = Capitalize<Lowercase<ReplaceUnderscores<S>>>;

type FormattedEnumValue<T> = FormattedEnumKey<Extract<keyof T, string>>;

const formatEnumValueImpl = <T>(value: ValueOf<T>, definedEnum: EnumType & T): FormattedEnumValue<T> => rPipe(
  getEnumKey<T>(value, definedEnum),
  String,
  rSplit('_'),
  rJoin(' '),
  rToLowerCase(),
  rCapitalize(),
) as FormattedEnumValue<T>;

export function formatEnumValue<T>(value: ValueOf<T>, definedEnum: EnumType & T): FormattedEnumValue<T>;
export function formatEnumValue<T>(definedEnum: EnumType & T): (value: ValueOf<T>) => FormattedEnumValue<T>;
export function formatEnumValue<T>(...args: readonly unknown[]): unknown {
  return rPurry(formatEnumValueImpl<T>, args);
}

export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) {
    throw new Error('Invalid byte value');
  }

  if (bytes === 0) {
    return '0B';
  }

  const units = [ 'B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB' ];
  const base = 1024;

  const exponent = Math.floor(Math.log(bytes) / Math.log(base));
  const value = bytes / Math.pow(base, exponent);

  const formatted = value % 1 === 0 ? value.toString() : value.toFixed(2).replace(/\.?0+$/, '');

  return `${formatted}${units[exponent]}`;
}
